import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MessageComposer = ({ 
  contact, 
  messageType = 'sms', 
  onSend, 
  onCancel,
  isVisible = false 
}) => {
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);

  const messageTemplates = {
    sms: [
      {
        value: 'casual_checkin',
        label: 'Casual Check-in',
        content: `Hey {name}! Hope you're doing well. Just wanted to check in and see how things are going with you. Would love to catch up soon!`
      },
      {
        value: 'professional_followup',label: 'Professional Follow-up',
        content: `Hi {name}, I hope this message finds you well. I was thinking about our last conversation and wanted to follow up. How have things been progressing?`
      },
      {
        value: 'friendly_reconnect',label: 'Friendly Reconnect',
        content: `Hi {name}! It's been a while since we last spoke. I was just thinking about you and wondered how you've been. Hope all is well!`
      },
      {
        value: 'custom',label: 'Custom Message',content: ''
      }
    ],
    email: [
      {
        value: 'professional_checkin',label: 'Professional Check-in',
        content: `Subject: Checking in\n\nHi {name},\n\nI hope this email finds you well. I wanted to reach out and see how things have been going for you lately.\n\nIt would be great to catch up and hear about what you've been working on.\n\nBest regards`
      },
      {
        value: 'casual_update',
        label: 'Casual Update',
        content: `Subject: Long time no talk!\n\nHey {name},\n\nI realized it's been way too long since we last connected! I'd love to hear what you've been up to and share some updates from my end too.\n\nHope you're doing amazing!\n\nCheers`
      },
      {
        value: 'custom',
        label: 'Custom Email',
        content: ''
      }
    ],
    whatsapp: [
      {
        value: 'quick_hello',
        label: 'Quick Hello',
        content: `Hey {name}! 👋 Hope you're having a great day. Just wanted to say hi and see how you're doing!`
      },
      {
        value: 'catch_up',
        label: 'Catch Up',
        content: `Hi {name}! It's been too long since we last chatted. How have you been? Would love to catch up over coffee sometime! ☕`
      },
      {
        value: 'custom',label: 'Custom WhatsApp',content: ''
      }
    ]
  };

  const templates = messageTemplates?.[messageType] || [];

  const handleTemplateChange = (templateValue) => {
    setSelectedTemplate(templateValue);
    const template = templates?.find(t => t?.value === templateValue);
    if (template && template?.content) {
      const personalizedMessage = template?.content?.replace(/{name}/g, contact?.name || 'there');
      setMessage(personalizedMessage);
    } else {
      setMessage('');
    }
  };

  const handleSend = () => {
    if (!message?.trim()) return;

    const messageData = {
      type: messageType,
      content: message,
      contactId: contact?.id,
      contactName: contact?.name,
      scheduled: isScheduled,
      scheduleDate: isScheduled ? scheduleDate : null,
      scheduleTime: isScheduled ? scheduleTime : null,
      timestamp: new Date()?.toISOString()
    };

    onSend(messageData);
    
    // Reset form
    setMessage('');
    setSelectedTemplate('');
    setScheduleDate('');
    setScheduleTime('');
    setIsScheduled(false);
  };

  const getMessageTypeIcon = () => {
    switch (messageType) {
      case 'sms': return 'MessageSquare';
      case 'email': return 'Mail';
      case 'whatsapp': return 'MessageCircle';
      default: return 'MessageSquare';
    }
  };

  const getMessageTypeLabel = () => {
    switch (messageType) {
      case 'sms': return 'SMS Message';
      case 'email': return 'Email Message';
      case 'whatsapp': return 'WhatsApp Message';
      default: return 'Message';
    }
  };

  if (!isVisible || !contact) return null;

  return (
    <div className="bg-surface border border-border rounded-xl p-6 shadow-card animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Icon name={getMessageTypeIcon()} size={20} color="white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">
              {getMessageTypeLabel()}
            </h3>
            <p className="text-sm text-text-secondary">
              To: {contact?.name}
            </p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
        >
          <Icon name="X" size={20} />
        </Button>
      </div>
      {/* Template Selection */}
      <div className="mb-4">
        <Select
          label="Choose Template"
          options={templates?.map(template => ({
            value: template?.value,
            label: template?.label
          }))}
          value={selectedTemplate}
          onChange={handleTemplateChange}
          placeholder="Select a message template..."
        />
      </div>
      {/* Message Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-text-primary mb-2">
          Message Content
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e?.target?.value)}
          placeholder={`Write your ${messageType} message here...`}
          rows={messageType === 'email' ? 8 : 4}
          className="w-full px-3 py-2 border border-border rounded-lg bg-input text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
        />
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-text-secondary">
            {message?.length} characters
          </span>
          {messageType === 'sms' && (
            <span className="text-xs text-text-secondary">
              {Math.ceil(message?.length / 160)} SMS{message?.length > 160 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
      {/* Schedule Options */}
      <div className="mb-6">
        <div className="flex items-center space-x-2 mb-3">
          <input
            type="checkbox"
            id="schedule-message"
            checked={isScheduled}
            onChange={(e) => setIsScheduled(e?.target?.checked)}
            className="rounded border-border text-primary focus:ring-ring"
          />
          <label htmlFor="schedule-message" className="text-sm font-medium text-text-primary">
            Schedule for later
          </label>
        </div>

        {isScheduled && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
            <Input
              type="date"
              label="Date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e?.target?.value)}
              min={new Date()?.toISOString()?.split('T')?.[0]}
            />
            <Input
              type="time"
              label="Time"
              value={scheduleTime}
              onChange={(e) => setScheduleTime(e?.target?.value)}
            />
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="default"
          onClick={handleSend}
          disabled={!message?.trim()}
          iconName="Send"
          iconPosition="left"
          className="flex-1"
        >
          {isScheduled ? 'Schedule Message' : 'Send Now'}
        </Button>
        
        <Button
          variant="outline"
          onClick={onCancel}
          className="flex-1 sm:flex-none"
        >
          Cancel
        </Button>
      </div>
      {/* Preview for Email */}
      {messageType === 'email' && message && (
        <div className="mt-4 p-4 bg-muted rounded-lg">
          <p className="text-xs font-medium text-text-secondary mb-2">Preview:</p>
          <div className="text-sm text-text-primary whitespace-pre-wrap">
            {message}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageComposer;